import clsx from 'clsx';
import { filter, map } from 'lodash';
import React, { useMemo, useState } from 'react';

import { RadioGroup } from '@headlessui/react';

import { ActionKind, useProductDispatch, useProductState } from '../store';
import SizeGuide from './SizeGuide';
import { useTranslation } from 'react-i18next';
import RestockForm from './RestockForm';
import { sortSizes } from '@/helpers/products';

const SizePicker = () => {
  const { t } = useTranslation();
  const dispatch = useProductDispatch();
  const { product, size, color } = useProductState();

  const sizes = useMemo(() => {
    const variations = filter(
      product.variations,
      ({ attribute_color }) => attribute_color === color
    ).sort(({ attribute_size: size1 }, { attribute_size: size2 }) => {
      return sortSizes(size1, size2);
    });

    return map(variations, variation => {
      const isInStock =
        Number(variation.stock?.in_stock) === 1 &&
        Number(variation.stock?.stock_quantity) > 0;

      return {
        label: variation.label_size,
        value: variation.attribute_size,
        disabled: !isInStock
      };
    });
  }, [product, color]);

  function setSize(size: string) {
    dispatch({ type: ActionKind.SET_SIZE, payload: size });
  }

  return (
    <RadioGroup value={size} onChange={setSize} className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm font-bold uppercase tracking-uyn">
          {t('P_PDP.OVERVIEW.SIZE')}
        </span>
        <SizeGuide />
      </div>

      <ul className="grid grid-cols-3 md:grid-cols-6 xl:grid-cols-8 gap-2">
        {sizes.map(size => (
          <RadioGroup.Option key={size.value} as="li" value={size.value}>
            {({ checked }) => (
              <div className="relative">
                <button
                  className={clsx(
                    'w-full border border-gray-300 text-center rounded py-3 disabled:border-gray-200 disabled:text-gray-700',
                    {
                      'ring-2 ring-black font-bold': checked
                    }
                  )}
                >
                  {size.label}
                </button>

                {size.disabled && (
                  <button className="absolute top-1 right-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4 text-gray-700 fill-white"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0M3.124 7.5A8.969 8.969 0 015.292 3m13.416 0a8.969 8.969 0 012.168 4.5"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </RadioGroup.Option>
        ))}
      </ul>
    </RadioGroup>
  );
};

export default SizePicker;
